import { AvatarStack, type AvatarStackPerson } from '@amelio/ui-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ContributorsDialog from './ContributorsDialog';

import type { User } from '@/types/directory/User';
import type { ObjectiveOrDraft } from '@/types/objectives';

import { useObjectiveAbilities } from '@/hooks/objectives/useObjectiveAbilities';
import i18n from '@/plugins/i18n';
import { toReaderLocale } from '@/utils/numberFormat';
import { objectiveHolderIds, orderHoldersForReader } from '@/utils/objectiveHolders';
import { getFullName, getInitials, getProfilePictureUrl } from '@/utils/user';

/** Up to this many, the page header writes every name beside the faces. */
const NAMED_AT_MOST = 3;

const TEXT_CLASS_NAME = 'text-navy text-sm font-medium';

interface ObjectiveContributorsProps {
    objective: ObjectiveOrDraft;
    employeesById: Map<string, User>;
    /**
     * `card`: a labelled stack whose touch lists everyone. `header`: the names written beside the
     * stack while they fit, else their count, the stack and the count then listing everyone.
     */
    placement: 'card' | 'header';
}

/**
 * The people of an objective, all on the same footing: the reader first, then everyone by name.
 * The full list is always one touch away, since a touch screen has no hover to reveal a name.
 */
export default function ObjectiveContributors({
    objective,
    employeesById,
    placement,
}: ObjectiveContributorsProps) {
    const { t } = useTranslation();
    const { currentUserId } = useObjectiveAbilities();
    const [isListOpen, setIsListOpen] = useState(false);

    const contributors: AvatarStackPerson[] = orderHoldersForReader(
        objectiveHolderIds(objective)
            .map((id) => employeesById.get(id))
            .filter((user): user is User => !!user),
        currentUserId,
    ).map((user) => ({
        id: user.id,
        name:
            user.id === currentUserId
                ? t('objectives.contributors.you', { name: getFullName(user) })
                : getFullName(user),
        src: getProfilePictureUrl(user.profilePictureUrl),
        initials: getInitials(user),
    }));

    // Nothing to show without a single contributor: no orphan label, no empty stack.
    if (contributors.length === 0) {
        return null;
    }

    const count = contributors.length;
    const namesWritten = placement === 'header' && count <= NAMED_AT_MOST;

    return (
        <div className={'flex items-center gap-3'}>
            {placement === 'card' && (
                <span className={TEXT_CLASS_NAME}>{t('objectives.card.contributorsLabel')}</span>
            )}

            {/* The list card opens the objective on any click; the stack and its dialog, a portal
                that still bubbles through React, stop theirs here. */}
            <div onClick={(event) => event.stopPropagation()}>
                {namesWritten ? (
                    <AvatarStack
                        people={contributors}
                        label={t('objectives.contributors.label', { count })}
                        caption={
                            <span className={TEXT_CLASS_NAME}>
                                {new Intl.ListFormat(toReaderLocale(i18n.language), {
                                    type: 'conjunction',
                                }).format(contributors.map((contributor) => contributor.name))}
                            </span>
                        }
                    />
                ) : (
                    <AvatarStack
                        people={contributors}
                        label={t('objectives.contributors.openList', { count })}
                        caption={
                            placement === 'header' && (
                                <span className={TEXT_CLASS_NAME}>
                                    {t('objectives.contributors.count', { count })}
                                </span>
                            )
                        }
                        onOpen={() => setIsListOpen(true)}
                    />
                )}

                <ContributorsDialog
                    isOpen={isListOpen}
                    onClose={() => setIsListOpen(false)}
                    contributors={contributors}
                />
            </div>
        </div>
    );
}
